import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import PageHeader from '../components/PageHeader'
import TableFilters from '../components/TableFilters'
import AddServiceModal from '../components/AddServiceModal'

const EMPTY = { name:'', phone:'', email:'', city:'', status:'Active', notes:'' }

const DEMO = []

function statusBadge(s) {
  const map = { Active:'badge-green', Pipeline:'badge-amber', Paused:'badge-red', Closed:'badge-red', Inactive:'badge-gray' }
  return 'badge ' + (map[s] || 'badge-gray')
}


function stageBadgeClass(stage) {
  if (!stage) return null
  const s = stage.toLowerCase()
  if (s === 'in progress') return 'badge badge-blue'
  if (s === 'completed') return 'badge badge-green'
  if (s === 'on hold' || s === 'paused' || s === 'closed') return 'badge badge-amber'
  return 'badge badge-gray'
}

function fmtPhone(p) {
  if (!p) return '—'
  const digits = p.replace(/\D/g, '').slice(-10)
  if (digits.length !== 10) return p
  return '+91 ' + digits.slice(0,5) + ' ' + digits.slice(5)
}

export default function Clients() {
  const [clients, setClients] = useState(DEMO)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [filtered, setFiltered] = useState([])
  const [expanded, setExpanded] = useState(null)
  const [servicesMap, setServicesMap] = useState({}) // brand_id → [service_type, ...]

  const CLIENT_FILTERS = [
    { key: 'search', label: 'Search', type: 'text', fields: ['name', 'city', 'email', 'project_names'] },
    { key: 'status', label: 'Status', type: 'select', options: ['Active', 'Pipeline', 'Paused', 'Inactive', 'Closed'] },
  ]
  const handleFiltered = useCallback(d => setFiltered(d), [])

  // Project state — now uses AddServiceModal
  const [serviceModal, setServiceModal] = useState(false)  // add service
  const [serviceClientId, setServiceClientId] = useState(null)
  const [stageMap, setStageMap] = useState({})

  // Pause project from Clients
  const [pauseModal, setPauseModal] = useState(false)
  const [pauseBrand, setPauseBrand] = useState(null)
  const [pauseClient, setPauseClient] = useState(null)
  const [pauseStatus, setPauseStatus] = useState('Paused')
  const [pauseReason, setPauseReason] = useState('')
  const [pausing, setPausing] = useState(false)

  useEffect(() => {
    async function load() {
      const [cRes, bRes, svcRes] = await Promise.all([
        supabase.from('clients').select('*').order('name'),
        supabase.from('brands').select('*').order('name'),
        supabase.from('services').select('brand_id, service_type, status'),
      ])
      // Build stageMap keyed by project_name lowercase (from services table)
      const sm = {}
      const svcMap = {}
      ;(svcRes.data || []).forEach(r => {
        if (!svcMap[r.brand_id]) svcMap[r.brand_id] = []
        svcMap[r.brand_id].push({ type: r.service_type, status: r.status })
        const brand = (bRes.data || []).find(b => b.id === r.brand_id)
        if (brand) {
          const k = brand.name.trim().toLowerCase()
          sm[k] = { stage: r.status, source: r.service_type.toLowerCase() }
        }
      })
      setStageMap(sm)
      setServicesMap(svcMap)
      const cData = cRes.data || []
      const bData = bRes.data || []
      if (cData.length) {
        const projectsMap = {}
        bData.forEach(b => {
          if (!projectsMap[b.client_id]) projectsMap[b.client_id] = []
          projectsMap[b.client_id].push(b)
        })
        setClients(cData.map(c => {
          const projs = projectsMap[c.id] || []
          return { ...c, projects: projs, project_names: projs.map(b => b.name).join(' ') }
        }))
      }
    }
    load()
  }, [])


  function openAdd() { setForm(EMPTY); setEditing(null); setModal(true) }
  function openEdit(c) {
    setForm({ name:c.name, phone:c.phone||'', email:c.email||'', city:c.city||'', status:c.status||'Active', notes:c.notes||'' })
    setEditing(c.id); setModal(true)
  }

  async function save() {
    setSaving(true)
    const payload = { ...form, phone: form.phone.replace(/\D/g, '').slice(-10) }
    if (editing) {
      const { data } = await supabase.from('clients').update(payload).eq('id', editing).select()
      setClients(prev => prev.map(c => c.id === editing ? { ...(data?.[0] || { ...c, ...payload }), projects: c.projects, project_names: c.project_names } : c))
    } else {
      const { data } = await supabase.from('clients').insert([payload]).select()
      const newClient = data?.[0] || { ...payload, id: Date.now() }
      setClients(prev => [...prev, { ...newClient, projects: [] }])
    }
    setSaving(false); setModal(false)
  }

  async function remove(id) {
    if (!window.confirm('Delete this client and all their projects?')) return
    await supabase.from('clients').delete().eq('id', id)
    setClients(prev => prev.filter(c => c.id !== id))
  }

  function toggleExpand(id) { setExpanded(prev => prev === id ? null : id) }

  // Open AddServiceModal for adding a service to an existing client
  function openAddProject(clientId) {
    setServiceClientId(clientId)
    setServiceModal(true)
  }

  function onServiceSaved(service) {
    // Refresh brand list so new brands created inside AddServiceModal appear
    supabase.from('brands').select('*').order('name').then(({ data: bData }) => {
      supabase.from('services').select('brand_id, service_type, status').then(({ data: svcData }) => {
        const sm2 = {}
        ;(svcData || []).forEach(r => {
          if (!sm2[r.brand_id]) sm2[r.brand_id] = []
          sm2[r.brand_id].push({ type: r.service_type, status: r.status })
        })
        setServicesMap(sm2)
        setClients(prev => prev.map(c => {
          const projs = (bData || []).filter(b => b.client_id === c.id)
          return { ...c, projects: projs, project_names: projs.map(b => b.name).join(' ') }
        }))
      })
    })
    setServiceModal(false)
  }

  async function removeProject(projId, clientId) {
    if (!window.confirm('Delete this project?')) return
    await supabase.from('brands').delete().eq('id', projId)
    setClients(prev => prev.map(c => c.id === clientId ? { ...c, projects: (c.projects||[]).filter(p => p.id !== projId) } : c))
  }

  function openPause(brand, client) {
    setPauseBrand(brand)
    setPauseClient(client)
    setPauseStatus('Paused')
    setPauseReason('')
    setPauseModal(true)
  }

  async function doPause() {
    if (!pauseBrand || !pauseClient) return
    setPausing(true)
    // Update all services for this brand to Paused/Stopped status
    await supabase.from('services').update({ status: pauseStatus }).eq('brand_id', pauseBrand.id)
    // Update servicesMap locally
    setServicesMap(prev => {
      const next = { ...prev }
      if (next[pauseBrand.id]) {
        next[pauseBrand.id] = next[pauseBrand.id].map(s => ({ ...s, status: pauseStatus }))
      }
      return next
    })
    // Update stageMap
    setStageMap(prev => {
      const next = { ...prev }
      const k = pauseBrand.name.trim().toLowerCase()
      if (next[k]) next[k] = { ...next[k], stage: pauseStatus }
      return next
    })
    setPausing(false)
    setPauseModal(false)
  }

  function printClient(c) {
    const win = window.open('', '_blank', 'width=820,height=700')
    if (!win) return
    const projs = c.projects || []
    const rows = projs.map(b => {
      const si = stageMap[b.name.trim().toLowerCase()]
      return `<tr><td>${b.name}</td><td>${b.category}</td><td>${b.billing_period || 'Monthly'}</td><td>${si ? si.stage : 'Not tracked'}</td><td>${b.notes || '—'}</td></tr>`
    }).join('')
    win.document.write(`<!DOCTYPE html><html><head><title>${c.name} — Client Summary</title>
<style>body{font-family:system-ui,sans-serif;padding:48px;color:#111;font-size:14px;max-width:720px;margin:0 auto}
h1{font-size:22px;margin:0 0 4px}
.meta{color:#666;font-size:13px;margin-bottom:28px;line-height:1.8}
table{width:100%;border-collapse:collapse;margin-top:12px}
th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#888;padding:8px 12px 8px 0;border-bottom:2px solid #eee}
td{padding:10px 12px 10px 0;border-bottom:1px solid #eee;font-size:13px}
.sec{font-size:13px;font-weight:600;margin:28px 0 8px}
.no-data{color:#aaa;padding:20px 0;text-align:center}
.print-btn{margin-top:28px;padding:8px 24px;cursor:pointer;font-size:13px}
@media print{.print-btn{display:none}}</style></head>
<body>
<h1>${c.name}</h1>
<div class="meta">
  ${c.status ? `Status: ${c.status}` : ''}
  ${c.phone ? ` &nbsp;·&nbsp; +91 ${c.phone}` : ''}
  ${c.email ? ` &nbsp;·&nbsp; ${c.email}` : ''}
  ${c.city ? ` &nbsp;·&nbsp; ${c.city}` : ''}
  ${c.notes ? `<br><em>${c.notes}</em>` : ''}
</div>
<div class="sec">Projects (${projs.length})</div>
<table><thead><tr><th>Project</th><th>Category</th><th>Billing</th><th>Stage</th><th>Notes</th></tr></thead>
<tbody>${rows || `<tr><td colspan="5" class="no-data">No projects</td></tr>`}</tbody></table>
<button class="print-btn" onclick="window.print()">Print / Save as PDF</button>
</body></html>`)
    win.document.close()
  }

  const totalProjects = clients.reduce((s, c) => s + (c.projects || []).length, 0)
  const INACTIVE_STATUSES = ['Paused', 'Inactive', 'Closed']
  const activeFiltered = filtered.filter(c => !INACTIVE_STATUSES.includes(c.status))
  const inactiveFiltered = filtered.filter(c => INACTIVE_STATUSES.includes(c.status))
  const [inactiveExpanded, setInactiveExpanded] = useState(false)

  function renderClientRows(list) {
    return list.map(c => (
      <React.Fragment key={c.id}>
        <tr style={{ cursor:'pointer' }} onClick={() => toggleExpand(c.id)}>
          <td style={{ textAlign:'center', color:'var(--text3)', fontSize:14, userSelect:'none' }}>{expanded === c.id ? '▾' : '▸'}</td>
          <td style={{ fontWeight:500 }}>{c.name}{(c.projects||[]).length > 0 && <span style={{ color:'var(--text3)', fontWeight:400, fontSize:12, marginLeft:4 }}>({(c.projects||[]).length})</span>}{(!c.phone && !c.email && !c.city) && <span className="badge badge-amber" style={{ marginLeft:6, fontSize:10 }}>Incomplete</span>}</td>
          <td style={{ fontFamily:'var(--mono)', fontSize:12 }}>{fmtPhone(c.phone)}</td>
          <td style={{ color:'var(--text2)', fontSize:12 }}>{c.email || '—'}</td>
          <td>{c.city || '—'}</td>
          <td><span className={statusBadge(c.status)}>{c.status}</span></td>
          <td>
            {(c.projects||[]).length === 0 ? <span style={{ color:'var(--text3)' }}>—</span> : (
              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                {(c.projects||[]).map(b => {
                  const svcTypeBadge = { Marketing:'badge-blue', Development:'badge-purple', Hosting:'badge-green' }
                  const brandSvcs = servicesMap[b.id] || []
                  const topType = brandSvcs[0]?.type
                  return (
                    <span key={b.id} className={`badge ${svcTypeBadge[topType] || 'badge-gray'}`} style={{ fontSize:11 }}>{b.name}</span>
                  )
                })}
              </div>
            )}
          </td>
          <td>
            <div style={{ display:'flex', gap:6 }} onClick={e => e.stopPropagation()}>
              <button className="btn" style={{ padding:'4px 10px', fontSize:12 }} onClick={() => openEdit(c)}>Edit</button>
              <button className="btn btn-danger" style={{ padding:'4px 10px', fontSize:12 }} onClick={() => remove(c.id)}>Del</button>
            </div>
          </td>
        </tr>
        {expanded === c.id && (
          <tr>
            <td colSpan={8} style={{ padding:0, background:'var(--surface2)' }}>
              <div style={{ padding:'12px 20px 12px 44px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                  <span style={{ fontSize:12, fontWeight:600, color:'var(--text2)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Projects ({(c.projects||[]).length})</span>
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn" style={{ padding:'3px 10px', fontSize:11 }} onClick={e => { e.stopPropagation(); printClient(c) }}>Print summary</button>
                    <button className="btn" style={{ padding:'3px 10px', fontSize:11 }} onClick={() => openAddProject(c.id)}>+ Add project</button>
                  </div>
                </div>
                {(c.projects||[]).length === 0 && (
                  <div style={{ color:'var(--text3)', fontSize:12, padding:'8px 0' }}>No projects yet. Add one to start tracking.</div>
                )}
                {(c.projects||[]).map(b => {
                  const brandSvcs = servicesMap[b.id] || []
                  const stageInfo = stageMap[b.name.trim().toLowerCase()]
                  const badgeCls = stageInfo ? stageBadgeClass(stageInfo.stage) : null
                  const svcTypeBadge = { Marketing:'badge-blue', Development:'badge-purple', Hosting:'badge-green' }
                  return (
                    <div key={b.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
                      <span style={{ fontWeight:500, fontSize:13, minWidth:140 }}>{b.name}</span>
                      {brandSvcs.length > 0
                        ? brandSvcs.map((svc, si) => (
                            <span key={si} className={`badge ${svcTypeBadge[svc.type] || 'badge-gray'}`} style={{ fontSize:11 }}>{svc.type}</span>
                          ))
                        : <span className="badge badge-gray" style={{ fontSize:11 }}>No services</span>
                      }
                      {badgeCls
                        ? <span className={badgeCls}>{stageInfo.stage}</span>
                        : <span style={{ color:'var(--text3)', fontSize:11 }}>Not tracked</span>
                      }
                      {b.notes && <span style={{ color:'var(--text2)', fontSize:12 }}>{b.notes}</span>}
                      <div style={{ marginLeft:'auto', display:'flex', gap:4 }}>
                        <button className="btn" style={{ padding:'2px 8px', fontSize:11 }} onClick={() => openAddProject(c.id)}>+ Service</button>
                        <button className="btn" style={{ padding:'2px 8px', fontSize:11, color:'var(--warning)', borderColor:'var(--warning)' }} onClick={e => { e.stopPropagation(); openPause(b, c) }}>Pause</button>
                        <button className="btn btn-danger" style={{ padding:'2px 8px', fontSize:11 }} onClick={() => removeProject(b.id, c.id)}>Del</button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    ))
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1 }}>
      <PageHeader
        title="Clients"
        subtitle={`${clients.length} clients · ${totalProjects} projects`}
        action={<button className="btn btn-primary" onClick={openAdd}>+ Add client</button>}
      />
      <div style={{ padding:28, flex:1 }}>
        <div style={{ marginBottom:16 }}>
          <TableFilters filters={CLIENT_FILTERS} data={clients} onFiltered={handleFiltered} />
        </div>

        {/* Active clients */}
        <div className="card">
          <table>
            <thead>
              <tr><th style={{ width:30 }}></th><th>Name</th><th>Phone</th><th>Email</th><th>City</th><th>Status</th><th>Projects</th><th style={{ width:100 }}></th></tr>
            </thead>
            <tbody>
              {renderClientRows(activeFiltered)}
              {activeFiltered.length === 0 && <tr><td colSpan={8} style={{ textAlign:'center', color:'var(--text3)', padding:32 }}>No active clients</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Inactive / Paused / Closed clients */}
        {inactiveFiltered.length > 0 && (
          <div style={{ marginTop:24 }}>
            <button
              onClick={() => setInactiveExpanded(v => !v)}
              style={{ display:'flex', alignItems:'center', gap:8, background:'none', border:'none', cursor:'pointer', padding:'4px 0', marginBottom:10, color:'var(--text2)', fontFamily:'var(--font)' }}
            >
              <span style={{ fontSize:13, userSelect:'none' }}>{inactiveExpanded ? '▾' : '▸'}</span>
              <span style={{ fontSize:13, fontWeight:600 }}>Inactive / Paused / Closed</span>
              <span className="badge badge-gray" style={{ fontSize:11 }}>{inactiveFiltered.length}</span>
            </button>
            {inactiveExpanded && (
              <div className="card">
                <table>
                  <thead>
                    <tr><th style={{ width:30 }}></th><th>Name</th><th>Phone</th><th>Email</th><th>City</th><th>Status</th><th>Projects</th><th style={{ width:100 }}></th></tr>
                  </thead>
                  <tbody>
                    {renderClientRows(inactiveFiltered)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}


        {/* Client notes summary */}
        {clients.some(c => c.notes) && (
          <div style={{ marginTop:20 }}>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Notes</div>
            <div className="card">
              {clients.filter(c => c.notes).map(c => (
                <div key={c.id} style={{ padding:'10px 16px', borderBottom:'1px solid var(--border)', display:'flex', gap:12 }}>
                  <span style={{ fontWeight:500, minWidth:120 }}>{c.name}</span>
                  <span style={{ color:'var(--text2)', fontSize:13 }}>{c.notes}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Client modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>{editing ? 'Edit client' : 'Add client'}</h3>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-field"><label>Full name *</label><input value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="Person name" /></div>
              <div className="form-row">
                <div className="form-field">
                  <label>Phone</label>
                  <div style={{ display:'flex', alignItems:'center', gap:0 }}>
                    <span style={{ padding:'7px 10px', background:'var(--surface2)', border:'1px solid var(--border-strong)', borderRight:'none', borderRadius:'6px 0 0 6px', fontSize:13, color:'var(--text2)', fontFamily:'var(--mono)' }}>+91</span>
                    <input
                      style={{ borderRadius:'0 6px 6px 0', flex:1 }}
                      value={form.phone}
                      onChange={e => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 10)
                        setForm({...form, phone:v})
                      }}
                      placeholder="10 digit number"
                      maxLength={10}
                    />
                  </div>
                </div>
                <div className="form-field"><label>Email</label><input type="email" value={form.email} onChange={e => setForm({...form, email:e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-field"><label>City</label><input value={form.city} onChange={e => setForm({...form, city:e.target.value})} placeholder="e.g. Mumbai" /></div>
                <div className="form-field"><label>Status</label>
                  <select value={form.status} onChange={e => setForm({...form, status:e.target.value})}>
                    <option>Active</option><option>Pipeline</option><option>Paused</option><option>Inactive</option><option>Closed</option>
                  </select>
                </div>
              </div>
              <div className="form-field"><label>Notes</label><textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes:e.target.value})} style={{ resize:'vertical' }} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.name}>
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add service modal — uses unified AddServiceModal, client locked */}
      {serviceModal && serviceClientId && (
        <AddServiceModal
          lockedClientId={serviceClientId}
          onClose={() => setServiceModal(false)}
          onSaved={onServiceSaved}
        />
      )}

      {/* Pause project modal */}
      {pauseModal && pauseBrand && pauseClient && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPauseModal(false)}>
          <div className="modal" style={{ maxWidth:420 }}>
            <div className="modal-header">
              <h3>Pause / Close Project</h3>
              <button className="modal-close" onClick={() => setPauseModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ background:'var(--surface2)', borderRadius:'var(--radius)', padding:12, marginBottom:16, fontSize:13 }}>
                <strong>{pauseClient.name}</strong> — {pauseBrand.name}
                <div style={{ color:'var(--text2)', marginTop:4, fontSize:12 }}>{pauseBrand.category}</div>
              </div>
              <div className="form-field" style={{ marginBottom:12 }}>
                <label>Status</label>
                <select value={pauseStatus} onChange={e => setPauseStatus(e.target.value)}>
                  <option>Paused</option>
                  <option>Closed</option>
                </select>
              </div>
              <div className="form-field">
                <label>Reason (optional)</label>
                <textarea rows={2} value={pauseReason} onChange={e => setPauseReason(e.target.value)} placeholder="Why is this being paused/closed?" style={{ resize:'vertical' }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setPauseModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={doPause} disabled={pausing}>
                {pausing ? 'Saving…' : pauseStatus === 'Paused' ? 'Pause Project' : 'Close Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
